import { Component, HostListener, inject, Inject, OnDestroy, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subject } from 'rxjs';
import { take, takeUntil, filter, map } from 'rxjs/operators';
import { AuthService } from './services/auth/authService.service';
import { Router, NavigationEnd, RouterOutlet, ActivatedRoute } from '@angular/router';
import { HeaderComponent } from './home/shared/components/header/header.component';
import { SideMenuComponent } from './home/shared/components/side-menu/side-menu.component';
import { MenuStateService } from './home/core/services/menu-state.service';
import { MenuBootstrapService } from './home/core/services/menu-bootstrap.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { NotificationsService } from '@core/notifications/services/notifications.service';
import { PageTitleService } from '@core/services/page-title.service';
import { BroadcastChannelService } from './services/broadcast-channel.service';
import { OverlayContainer } from '@angular/cdk/overlay';
import { ModernHostComponent } from './core/modern-host/modern-host.component';
import { CurrentUserContextService } from './services/auth/current-user-context.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: true,
  imports: [SideMenuComponent, RouterOutlet, HeaderComponent, ModernHostComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  readonly menuStateService = inject(MenuStateService);
  readonly menuBootstrapService = inject(MenuBootstrapService);
  readonly notificationService = inject(NotificationsService);
  readonly #currentUserContext = inject(CurrentUserContextService);
  isMenuOpen = toSignal(this.menuStateService.isMenuOpen$, { initialValue: false });
  isLegacyRoute = true;
  isViewportShell = false;

  url: string = this.document.location.href;
  loginPath: boolean = false;

  base_url = window.location.origin;
  pathName = window.location.pathname.split('/');

  loaderSpin: boolean = false;

  user: any;

  innerWidth: any;
  websocketUrl: any;

  downloadable: boolean = false;

  previousUrl: string = '';

  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private tokenLogin: AuthService,
    private pageTitleService: PageTitleService,
    private broadcastChannelService: BroadcastChannelService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private overlayContainer: OverlayContainer
  ) {}

  private updateShellMode(url: string, data: Record<string, unknown>): void {
    const path = url.split(/[?#]/)[0];
    this.isViewportShell = !!data['newUI'] || path === '/home';
  }

  private syncOverlayClass(isLegacy: boolean): void {
    const el = this.overlayContainer.getContainerElement();
    if (isLegacy) {
      el.classList.add('ntt-basecoat-legacy');
      el.classList.remove('new-ui');
    } else {
      el.classList.remove('ntt-basecoat-legacy');
      el.classList.add('new-ui');
    }
  }

  ngOnInit(): void {
    this.menuBootstrapService.initialize();

    this.#currentUserContext.ready$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((context) => {
        if (context === null) return;
        this.notificationService.initialize(context.userId);
        this.chartUrl(window.location.pathname);
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event) => {
          const navigationEnd = event as NavigationEnd;
          let route = this.activatedRoute;
          while (route.firstChild) route = route.firstChild;
          return {
            url: navigationEnd.urlAfterRedirects,
            data: route.snapshot.data
          };
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(({ url, data }) => {
        this.chartUrl(url);
        this.updateShellMode(url, data);
        this.isLegacyRoute = !data['newUI'];
        this.syncOverlayClass(this.isLegacyRoute);
      });

    // Seed the overlay class for the initial route (before any NavigationEnd fires).
    {
      let route = this.activatedRoute;
      while (route.firstChild) route = route.firstChild;
      const data = route.snapshot.data;
      this.syncOverlayClass(!data['newUI']);
      this.updateShellMode(this.router.url, data);
    }

    // Inicialização baseada no path atual
    this.chartUrl(window.location.pathname);

    /** Listen for successful login messages from other tabs and reload if on login route */
    this.broadcastChannelService.loginSuccess$
      .pipe(
        filter((message) => message !== null && Date.now() - message.timestamp < 5000), // only react to recent login messages to avoid unnecessary reloads
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const pathName = window.location.pathname.split('/');
        if (pathName[1] === 'login') {
          window.location.reload();
        }
      });

    /** Listen for logout initiated messages from other tabs and trigger local logout */
    this.broadcastChannelService.logoutInitiated$
      .pipe(
        filter((message) => message !== null && Date.now() - message.timestamp < 5000),
        takeUntil(this.destroy$)
      )
      .subscribe((message) => {
        const pathName = window.location.pathname.split('/');
        if (pathName[1] !== 'login' && pathName[1] !== '') {
          this.tokenLogin.logout(message!.reason, true); // true = skip broadcast to avoid loops
        }
      });
  }

  chartUrl(url: string) {
    if (!url) return;

    let path = '';
    try {
      // Se for uma URL completa, extrai o pathname
      if (url.startsWith('http')) {
        path = new URL(url).pathname;
      } else {
        // Se for apenas um path, remove query params e fragmentos
        path = url.split(/[?#]/)[0];
      }
    } catch (e) {
      path = url;
    }

    path = path.toLowerCase();

    // Define se estamos em uma página de login ou na raiz (sem sistema logado)
    // Usamos regex ou verificações exatas para evitar falsos positivos
    if (path.includes('/login') || path === '/' || path === '') {
      this.loginPath = true;
      this.menuStateService.setMenuState(false);
    } else {
      this.loginPath = false;
    }
  }

  setLoader(url: string) {
    if (
      url.includes('/projects/details') ||
      url.includes('/teams') ||
      url.includes('/roles') ||
      url.includes('/users')
    ) {
      this.loaderSpin = !this.loaderSpin;

      setTimeout(() => {
        this.loaderSpin = false;
      }, 8000);
    }
  }

  @HostListener('window:resize', ['$event'])
  listenerWidth(event: any) {
    this.innerWidth = window.innerWidth;
    if (this.innerWidth > 1920) {
      location.reload();
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
